import React, {useEffect, useMemo, useState, useRef} from "react";
import { Card, Row, Col, Statistic, Tag, Divider, Select, Button, message } from "antd";
import {
    RiseOutlined,
    FallOutlined,
    BarChartOutlined,
    LeftOutlined,
    RightOutlined,
    FilePdfOutlined
} from '@ant-design/icons';
import moment from "moment";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import {instance} from "../../API/API";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const { Option } = Select;

/**
 * Компонент дашборда аналитики доходов
 * Отображает сводку, сравнения и графики доходов
 */
const IncomeDashboard = () => {

    const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1);
    const [selectedYear, setSelectedYear] = useState(moment().year());
    const [exporting, setExporting] = useState(false);

    const [income, setIncome] = useState(null)
    const [payments, setPayments] = useState(null)
    const [one_income, setOneIncome] = useState(null)

    const [loading, setLoading] = useState(false)

    const dashboardRef = useRef();

    useEffect(() => {
        const getInitialData = async () => {
            await getIncome()
            await getOneIncome()
            await getPayments()
        }

        const getIncome = async () => {
            const income_res = await instance.get('finance/getIncome.php?api-key=null')
            if (income_res && income_res.data){
                setIncome(income_res.data.income);
            }
        }
        const getOneIncome = async () => {
            const one_income_res = await instance.get('finance/getOneIncome.php?api-key=null')
            if (one_income_res && one_income_res.data){
                setOneIncome(one_income_res.data.one_income);
            }
        }
        const getPayments = async () => {
            const payments_res = await instance.get('finance/getPayment.php?api-key=null')
            if (payments_res && payments_res.data){
                setPayments(payments_res.data.payments);
            }
        }

        try {
            setLoading(true)
            getInitialData()
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }, [])

    /**
     * Функция для экспорта дашборда в PDF
     */
    const exportToPDF = async () => {
        if (!dashboardRef.current) return;

        setExporting(true);
        try {
            // Создаем canvas из содержимого дашборда
            const canvas = await html2canvas(dashboardRef.current, {
                scale: 2, // Увеличиваем качество
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff'
            });

            // Создаем PDF
            const pdf = new jsPDF('landscape', 'mm', 'a3');
            const imgData = canvas.toDataURL('image/png');

            // Получаем размеры страницы
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Рассчитываем размеры изображения для заполнения страницы
            const imgWidth = pageWidth - 40; // Отступы по 10мм с каждой стороны
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Добавляем заголовок
            pdf.setFontSize(16);
            pdf.setTextColor(40, 40, 40);
            pdf.text('Incomes analytics', 15, 15);

            pdf.setFontSize(10);
            pdf.setTextColor(100, 100, 100);
            pdf.text(`Generated: ${moment().format('DD.MM.YYYY HH:mm')}`, pageWidth - 15, 15, { align: 'right' });

            // Добавляем основное изображение
            pdf.addImage(imgData, 'PNG', 10, 25, imgWidth, imgHeight);

            // Сохраняем PDF
            pdf.save(`аналитика-доходов-${moment().format('YYYY-MM-DD')}.pdf`);

            message.success('PDF успешно сгенерирован');
        } catch (error) {
            console.error('Ошибка при генерации PDF:', error);
            message.error('Ошибка при генерации PDF');
        } finally {
            setExporting(false);
        }
    };

    /**
     * Основная функция анализа данных о доходах
     */
    const incomeAnalysis = useMemo(() => {
        if (!income || !payments || !one_income) return null;

        const currentDate = moment();
        const currentYear = currentDate.year();
        const currentMonth = currentDate.month() + 1;
        const lastYear = currentYear - 1;

        // Вспомогательные функции
        const parseCostString = (costStr) => {
            return costStr.split('`').map(cost =>
                parseFloat(cost.replace(',', '.')) || 0
            );
        };

        const getMonthCost = (incomeItem, month) => {
            const costs = parseCostString(incomeItem.cost);
            return costs[month - 1] || 0;
        };

        // Расчеты по периодическим доходам (планируемые)
        const calculatePeriodicIncome = (year, month) => {
            let total = 0;
            let activeObjects = 0;

            income.forEach(item => {
                if (item.enable === "1") {
                    activeObjects++;
                    total += getMonthCost(item, month);
                }
            });

            return { total, activeObjects };
        };

        // Расчеты по фактическим платежам (периодические доходы)
        const calculateActualPayments = (year, month) => {
            const monthlyPayments = payments.filter(payment => {
                const paymentDate = moment(payment.date * 1000);
                return paymentDate.year() === year && paymentDate.month() + 1 === month;
            });

            const total = monthlyPayments.reduce((sum, payment) =>
                sum + (parseFloat(payment.cost) || 0), 0
            );

            return { total, count: monthlyPayments.length };
        };

        // Расчеты по разовым доходам
        const calculateOneTimeIncome = (year, month) => {
            const monthlyOneTime = one_income.filter(income => {
                const incomeDate = moment(income.date * 1000);
                return incomeDate.year() === year && incomeDate.month() + 1 === month;
            });

            const total = monthlyOneTime.reduce((sum, item) =>
                sum + (parseFloat(item.cost) || 0), 0
            );

            return { total, count: monthlyOneTime.length };
        };

        // Данные за текущий год (с начала года по текущий месяц)
        const currentYearStats = [];
        for (let month = 1; month <= currentMonth; month++) {
            const periodicPlanned = calculatePeriodicIncome(currentYear, month);
            const periodicActual = calculateActualPayments(currentYear, month);
            const oneTimeActual = calculateOneTimeIncome(currentYear, month);

            currentYearStats.push({
                month,
                monthName: moment().month(month - 1).format('MMM'),
                planned: periodicPlanned.total,
                periodicActual: periodicActual.total,
                oneTimeActual: oneTimeActual.total,
                totalActual: periodicActual.total + oneTimeActual.total,
                paymentsCount: periodicActual.count + oneTimeActual.count
            });
        }

        // Данные за прошлый год (аналогичный период)
        const lastYearStats = [];
        for (let month = 1; month <= currentMonth; month++) {
            const periodicPlanned = calculatePeriodicIncome(lastYear, month);
            const periodicActual = calculateActualPayments(lastYear, month);
            const oneTimeActual = calculateOneTimeIncome(lastYear, month);

            lastYearStats.push({
                month,
                monthName: moment().month(month - 1).format('MMM'),
                planned: periodicPlanned.total,
                periodicActual: periodicActual.total,
                oneTimeActual: oneTimeActual.total,
                totalActual: periodicActual.total + oneTimeActual.total,
                paymentsCount: periodicActual.count + oneTimeActual.count
            });
        }

        // Подготовка данных для графика
        const chartData = currentYearStats.map((currentMonthData, index) => {
            const lastYearMonthData = lastYearStats[index] || {
                periodicActual: 0,
                oneTimeActual: 0
            };

            return {
                month: currentMonthData.monthName,
                currentPeriodic: currentMonthData.periodicActual,
                lastYearPeriodic: lastYearMonthData.periodicActual,
                currentOneTime: currentMonthData.oneTimeActual,
                lastYearOneTime: lastYearMonthData.oneTimeActual,
                currentPlanned: currentMonthData.planned,
                totalCurrent: currentMonthData.periodicActual + currentMonthData.oneTimeActual,
                totalLastYear: lastYearMonthData.periodicActual + lastYearMonthData.oneTimeActual
            };
        });

        // Расчет показателей для ВЫБРАННОГО месяца
        const selectedPeriodicPlanned = calculatePeriodicIncome(selectedYear, selectedMonth);
        const selectedPeriodicActual = calculateActualPayments(selectedYear, selectedMonth);
        const selectedOneTimeActual = calculateOneTimeIncome(selectedYear, selectedMonth);

        // Расчет показателей за аналогичный месяц прошлого года
        const sameMonthLastYearPeriodicPlanned = calculatePeriodicIncome(selectedYear - 1, selectedMonth);
        const sameMonthLastYearPeriodicActual = calculateActualPayments(selectedYear - 1, selectedMonth);
        const sameMonthLastYearOneTimeActual = calculateOneTimeIncome(selectedYear - 1, selectedMonth);

        // Суммы за период с начала года до ВЫБРАННОГО месяца
        const selectedYearToDate = {
            periodicPlanned: currentYearStats
                .filter(monthData => monthData.month <= selectedMonth)
                .reduce((sum, month) => sum + month.planned, 0),
            periodicActual: currentYearStats
                .filter(monthData => monthData.month <= selectedMonth)
                .reduce((sum, month) => sum + month.periodicActual, 0),
            oneTimeActual: currentYearStats
                .filter(monthData => monthData.month <= selectedMonth)
                .reduce((sum, month) => sum + month.oneTimeActual, 0),
            totalActual: currentYearStats
                .filter(monthData => monthData.month <= selectedMonth)
                .reduce((sum, month) => sum + month.totalActual, 0)
        };

        const lastYearToDate = {
            periodicPlanned: lastYearStats
                .filter(monthData => monthData.month <= selectedMonth)
                .reduce((sum, month) => sum + month.planned, 0),
            periodicActual: lastYearStats
                .filter(monthData => monthData.month <= selectedMonth)
                .reduce((sum, month) => sum + month.periodicActual, 0),
            oneTimeActual: lastYearStats
                .filter(monthData => monthData.month <= selectedMonth)
                .reduce((sum, month) => sum + month.oneTimeActual, 0),
            totalActual: lastYearStats
                .filter(monthData => monthData.month <= selectedMonth)
                .reduce((sum, month) => sum + month.totalActual, 0)
        };

        // Проценты изменений
        const sameMonthYearlyChangePercent = (sameMonthLastYearPeriodicActual.total + sameMonthLastYearOneTimeActual.total) > 0 ?
            ((selectedPeriodicActual.total + selectedOneTimeActual.total) - (sameMonthLastYearPeriodicActual.total + sameMonthLastYearOneTimeActual.total)) /
            (sameMonthLastYearPeriodicActual.total + sameMonthLastYearOneTimeActual.total) * 100 : 0;

        const yearlyChangePercent = lastYearToDate.totalActual > 0 ?
            ((selectedYearToDate.totalActual - lastYearToDate.totalActual) / lastYearToDate.totalActual * 100) : 0;

        return {
            selected: {
                planned: selectedPeriodicPlanned.total,
                actual: selectedPeriodicActual.total + selectedOneTimeActual.total,
                actualPeriodic: selectedPeriodicActual.total,
                periodic: selectedPeriodicPlanned.total,
                oneTime: selectedOneTimeActual.total,
                activeObjects: selectedPeriodicPlanned.activeObjects,
                paymentsCount: selectedPeriodicActual.count + selectedOneTimeActual.count
            },
            selectedYearToDate,
            lastYearToDate,
            changes: {
                sameMonthYearly: {
                    value: (selectedPeriodicActual.total + selectedOneTimeActual.total) - (sameMonthLastYearPeriodicActual.total + sameMonthLastYearOneTimeActual.total),
                    percent: sameMonthYearlyChangePercent,
                    isPositive: sameMonthYearlyChangePercent >= 0
                },
                yearly: {
                    value: selectedYearToDate.totalActual - lastYearToDate.totalActual,
                    percent: yearlyChangePercent,
                    isPositive: yearlyChangePercent >= 0
                }
            },
            chartData,
            currentMonth: selectedMonth,
            selectedMonth,
            selectedYear,
            sameMonthLastYear: {
                actual: sameMonthLastYearPeriodicActual.total + sameMonthLastYearOneTimeActual.total,
                periodicActual: sameMonthLastYearPeriodicActual.total,
                oneTimeActual: sameMonthLastYearOneTimeActual.total,
                periodicPlanned: sameMonthLastYearPeriodicPlanned.total
            }
        };
    }, [ income, payments, one_income, selectedMonth, selectedYear]);

    // Функции для переключения месяцев
    const handlePreviousMonth = () => {
        if (selectedMonth === 1) {
            setSelectedMonth(12);
            setSelectedYear(selectedYear - 1);
        } else {
            setSelectedMonth(selectedMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (selectedMonth === 12) {
            setSelectedMonth(1);
            setSelectedYear(selectedYear + 1);
        } else {
            setSelectedMonth(selectedMonth + 1);
        }
    };

    // Функция для получения названия месяца
    const getMonthName = (monthNumber) => {
        return moment().month(monthNumber - 1).format('MMMM');
    };

    if (loading) {
        return <div>Загрузка данных...</div>;
    }
    if (!incomeAnalysis) {
        return <div>Данные не загружены.</div>;
    }
    const { selected, changes, chartData, currentMonth, selectedYear: analysisYear, selectedYearToDate, lastYearToDate, sameMonthLastYear } = incomeAnalysis;

    // Конфигурация графика
    const renderChart = () => {
        return (
            <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                        dataKey="month"
                        stroke="#666"
                        fontSize={12}
                    />
                    <YAxis
                        stroke="#666"
                        fontSize={12}
                        tickFormatter={(value) => `${value} €`}
                    />
                    <Tooltip
                        formatter={(value, name) => [
                            `${parseFloat(value).toFixed(2)} €`,
                            name
                        ]}
                        labelFormatter={(label) => `Месяц: ${label}`}
                    />
                    <Legend />

                    <Line
                        type="monotone"
                        dataKey="currentPeriodic"
                        name={`Периодические ${moment().year()} (факт)`}
                        stroke="#1890ff"
                        strokeWidth={3}
                        dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6 }}
                    />

                    <Line
                        type="monotone"
                        dataKey="lastYearPeriodic"
                        name={`Периодические ${moment().year() - 1} (факт)`}
                        stroke="#69c0ff"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={{ fill: '#69c0ff', strokeWidth: 2, r: 4 }}
                    />

                    <Line
                        type="monotone"
                        dataKey="currentOneTime"
                        name={`Разовые ${moment().year()} (факт)`}
                        stroke="#52c41a"
                        strokeWidth={3}
                        dot={{ fill: '#52c41a', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6 }}
                    />

                    <Line
                        type="monotone"
                        dataKey="lastYearOneTime"
                        name={`Разовые ${moment().year() - 1} (факт)`}
                        stroke="#95de64"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={{ fill: '#95de64', strokeWidth: 2, r: 4 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        );
    };

    return (
        <div ref={dashboardRef}>
        <Card
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <BarChartOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                    <span>Аналитика доходов</span>
                    <Tag color="blue">Текущий месяц: {moment().format('MMMM YYYY')}</Tag>
                </div>
            }
            extra={
                <Button
                    type="primary"
                    icon={<FilePdfOutlined />}
                    onClick={exportToPDF}
                    loading={exporting}
                    style={{ background: '#ff4d4f', borderColor: '#ff4d4f' }}
                >
                    Экспорт в PDF
                </Button>
            }
            className="income-dashboard"
        >
            {/* Основной контент дашборда с ref для экспорта */}
                {/* Ключевые метрики */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col span={12}>
                        <Card
                            size="small"
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span>Доход за месяц</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Button
                                            size="small"
                                            icon={<LeftOutlined />}
                                            onClick={handlePreviousMonth}
                                            disabled={selectedYear <= moment().year() - 5}
                                        />
                                        <span style={{ fontWeight: 'bold', minWidth: '120px', textAlign: 'center' }}>
                                            {getMonthName(selectedMonth)} {analysisYear}
                                        </span>
                                        <Button
                                            size="small"
                                            icon={<RightOutlined />}
                                            onClick={handleNextMonth}
                                            disabled={selectedYear > moment().year() || (selectedYear === moment().year() && selectedMonth > moment().month() + 1)}
                                        />
                                    </div>
                                </div>
                            }
                        >
                            <Statistic
                                title={`Фактический доход (${getMonthName(selectedMonth)} ${analysisYear})`}
                                value={selected.actual}
                                precision={2}
                                valueStyle={{ color: '#52c41a' }}
                                prefix="€"
                                suffix={
                                    <Tag color={changes.sameMonthYearly.isPositive ? 'success' : 'error'}>
                                        {changes.sameMonthYearly.isPositive ? <RiseOutlined /> : <FallOutlined />}
                                        {Math.abs(changes.sameMonthYearly.percent).toFixed(1)}%
                                    </Tag>
                                }
                            />
                            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                                vs {getMonthName(selectedMonth)} {analysisYear - 1}: {changes.sameMonthYearly.value.toFixed(2)}€
                            </div>

                            <Divider style={{ margin: '12px 0' }} />

                            <div style={{ fontSize: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <span>Периодические (факт):</span>
                                    <div style={{ textAlign: 'right' }}>
                                        <strong>{selected.actualPeriodic.toFixed(2)}€</strong>
                                        <div style={{
                                            color: selected.actualPeriodic >= sameMonthLastYear.periodicActual ? '#52c41a' : '#cf1322',
                                            fontSize: 11
                                        }}>
                                            {sameMonthLastYear.periodicActual > 0 ?
                                                `${(selected.actualPeriodic - sameMonthLastYear.periodicActual) >= 0 ? '+' : ''}${(selected.actualPeriodic - sameMonthLastYear.periodicActual).toFixed(2)}€`
                                                : '0.00€'}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <span>Разовые (факт):</span>
                                    <div style={{ textAlign: 'right' }}>
                                        <strong>{selected.oneTime.toFixed(2)}€</strong>
                                        <div style={{
                                            color: selected.oneTime >= sameMonthLastYear.oneTimeActual ? '#52c41a' : '#cf1322',
                                            fontSize: 11
                                        }}>
                                            {sameMonthLastYear.oneTimeActual > 0 ?
                                                `${(selected.oneTime - sameMonthLastYear.oneTimeActual) >= 0 ? '+' : ''}${(selected.oneTime - sameMonthLastYear.oneTimeActual).toFixed(2)}€`
                                                : '0.00€'}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Планируемые (периодические):</span>
                                    <div style={{ textAlign: 'right' }}>
                                        <strong>{selected.planned.toFixed(2)}€</strong>
                                        <div style={{
                                            color: selected.planned >= sameMonthLastYear.periodicPlanned ? '#52c41a' : '#cf1322',
                                            fontSize: 11
                                        }}>
                                            {sameMonthLastYear.periodicPlanned > 0 ?
                                                `${(selected.planned - sameMonthLastYear.periodicPlanned) >= 0 ? '+' : ''}${(selected.planned - sameMonthLastYear.periodicPlanned).toFixed(2)}€`
                                                : '0.00€'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>

                    <Col span={12}>
                        <Card size="small">
                            <Statistic
                                title={`Фактический доход (январь-${getMonthName(selectedMonth).toLowerCase()} ${analysisYear})`}
                                value={selectedYearToDate.totalActual}
                                precision={2}
                                valueStyle={{ color: '#1890ff' }}
                                prefix="€"
                                suffix={
                                    <Tag color={changes.yearly.isPositive ? 'success' : 'error'}>
                                        {changes.yearly.isPositive ? <RiseOutlined /> : <FallOutlined />}
                                        {Math.abs(changes.yearly.percent).toFixed(1)}%
                                    </Tag>
                                }
                            />
                            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                                vs аналогичный период прошлого года: {changes.yearly.value.toFixed(2)}€
                            </div>

                            <Divider style={{ margin: '12px 0' }} />

                            <div style={{ fontSize: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <span>Периодические (факт):</span>
                                    <div style={{ textAlign: 'right' }}>
                                        <strong>{selectedYearToDate.periodicActual.toFixed(2)}€</strong>
                                        <div style={{
                                            color: selectedYearToDate.periodicActual >= lastYearToDate.periodicActual ? '#52c41a' : '#cf1322',
                                            fontSize: 11
                                        }}>
                                            {lastYearToDate.periodicActual > 0 ?
                                                `${(selectedYearToDate.periodicActual - lastYearToDate.periodicActual) >= 0 ? '+' : ''}${(selectedYearToDate.periodicActual - lastYearToDate.periodicActual).toFixed(2)}€`
                                                : '0.00€'}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <span>Разовые (факт):</span>
                                    <div style={{ textAlign: 'right' }}>
                                        <strong>{selectedYearToDate.oneTimeActual.toFixed(2)}€</strong>
                                        <div style={{
                                            color: selectedYearToDate.oneTimeActual >= lastYearToDate.oneTimeActual ? '#52c41a' : '#cf1322',
                                            fontSize: 11
                                        }}>
                                            {lastYearToDate.oneTimeActual > 0 ?
                                                `${(selectedYearToDate.oneTimeActual - lastYearToDate.oneTimeActual) >= 0 ? '+' : ''}${(selectedYearToDate.oneTimeActual - lastYearToDate.oneTimeActual).toFixed(2)}€`
                                                : '0.00€'}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Планируемые (периодические):</span>
                                    <div style={{ textAlign: 'right' }}>
                                        <strong>{selectedYearToDate.periodicPlanned.toFixed(2)}€</strong>
                                        <div style={{
                                            color: selectedYearToDate.periodicPlanned >= lastYearToDate.periodicPlanned ? '#52c41a' : '#cf1322',
                                            fontSize: 11
                                        }}>
                                            {lastYearToDate.periodicPlanned > 0 ?
                                                `${(selectedYearToDate.periodicPlanned - lastYearToDate.periodicPlanned) >= 0 ? '+' : ''}${(selectedYearToDate.periodicPlanned - lastYearToDate.periodicPlanned).toFixed(2)}€`
                                                : '0.00€'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>

                <Divider />

                {/* Основной график */}
                <Card
                    title={`Динамика фактических доходов: ${moment().year()} vs ${moment().year() - 1} (период с января по ${moment().format('MMMM')})`}
                    size="small"
                    style={{ marginBottom: 24 }}
                >
                    {renderChart()}
                </Card>

                {/* Дополнительная статистика */}
                <Row gutter={[16, 16]}>
                    <Col span={8}>
                        <Card size="small" title="Эффективность">
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 12 }}>
                                <Tag color="blue" style={{ fontSize: '14px', padding: '8px 12px' }}>
                                    Активных объектов: <strong>{selected.activeObjects}</strong>
                                </Tag>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                                <Tag color="green" style={{ fontSize: '14px', padding: '8px 12px' }}>
                                    Платежей в месяц: <strong>{selected.paymentsCount}</strong>
                                </Tag>
                            </div>
                        </Card>
                    </Col>

                    <Col span={8}>
                        <Card size="small" title="Выполнение плана">
                            <Statistic
                                title="С начала года"
                                value={selectedYearToDate.periodicPlanned > 0 ? (selectedYearToDate.periodicActual / selectedYearToDate.periodicPlanned * 100) : 0}
                                precision={1}
                                valueStyle={{
                                    color: (selectedYearToDate.periodicActual / selectedYearToDate.periodicPlanned * 100) >= 100 ? '#52c41a' :
                                        (selectedYearToDate.periodicActual / selectedYearToDate.periodicPlanned * 100) >= 80 ? '#faad14' : '#cf1322'
                                }}
                                suffix="% выполнения"
                            />
                            <Divider style={{ margin: '12px 0' }} />
                            <Statistic
                                title="Темп роста"
                                value={changes.yearly.percent}
                                precision={1}
                                valueStyle={{ color: changes.yearly.isPositive ? '#52c41a' : '#cf1322' }}
                                suffix="%"
                            />
                        </Card>
                    </Col>

                    <Col span={8}>
                        <Card size="small" title="Средние показатели">
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                <span>Средний чек:</span>
                                <strong>
                                    {selected.paymentsCount > 0 ? (selected.actualPeriodic / selected.paymentsCount).toFixed(2) : 0}€
                                </strong>
                            </div>
                            <Divider style={{ margin: '12px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Средний доход/объект:</span>
                                <strong>
                                    {selected.activeObjects > 0 ? (selectedYearToDate.periodicActual / selected.activeObjects / currentMonth).toFixed(2) : 0}€
                                </strong>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Пояснительная информация */}
                <Divider />
                <div style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}>
                    <strong>Примечание:</strong> График отображает ФАКТИЧЕСКИЕ поступления доходов за период с января по текущий месяц.
                    Сплошные линии - текущий год, пунктирные - прошлый год. Синие - периодические доходы (платежи), зеленые - разовые доходы.
                </div>

        </Card>
        </div>
    );
};

export default IncomeDashboard;